/**
 * Story backgrounds — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_creator`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const storyBackgroundsPack: ToilePack = {
    slug: 'story-backgrounds',
    category: 'creator',
    name: 'Story backgrounds',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'gradient', label: 'Gradient', subject: 'a smooth two-tone gradient washing top to bottom, dusted evenly with fine film grain'},
        {key: 'checks', label: 'Checks', subject: 'a small even checkerboard of soft rounded squares kept modest against the tall frame'},
        {key: 'terrazzo', label: 'Terrazzo', subject: 'a loose terrazzo scatter of small confetti chips spaced evenly over a plain ground'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'flat matte color with no depth, the edges soft and slightly feathered', palette: 'Blush, butter yellow and pale mint, nothing saturated.', framing: 'The motif stays small and even so the middle band reads calm.'},
        {key: 'night', label: 'Night', style: 'deep dark tones under a faint haze with a dusting of tiny stars', palette: 'Ink blue and violet with a few pale silver specks.', framing: 'The interest gathers along the top and bottom edges, the centre left dark and open.'},
        {key: 'paper', label: 'Paper', style: 'a warm handmade paper surface with visible fibre tooth and softly deckled edges', palette: 'Oat, sand and faded terracotta on cream.', framing: 'The texture fills the tall frame evenly, the middle band held quiet.'},
    ],
    directions: ['a watercolor wash instead', 'in warm autumn colors', 'for a birthday story'],
    seo: {
        title: 'Instagram story backgrounds: gradient, checks, terrazzo',
        metaDescription: 'Nine story backgrounds — gradient, checks and terrazzo textures — in pastel, night and paper moods, phone-tall with the middle kept calm for text.',
        h1: 'Story background templates: gradient, checks and terrazzo in three moods',
        tagline: 'Nine backgrounds, three textures, three moods — the middle kept calm for a caption or screenshot.',
        intro: 'A story background has to leave room for the caption or screenshot on top of it. These nine are built that way: a gradient wash, a soft check pattern and a terrazzo scatter, each in a pastel, night or paper mood — phone-tall, tileable, with the middle band left quiet. Download the background, or ask for a different texture.',
        useCases: [
            'an Instagram or TikTok story backdrop',
            'a phone wallpaper',
            'a quote-card background behind text',
            'a consistent look across a week of stories',
        ],
        faq: [
            {q: 'What size does the background export at?', a: 'Each design renders at 768 by 1344 pixels, the same 9:16 phone-tall ratio as a story or a typical phone wallpaper.'},
            {q: 'Is the middle really clear enough for text?', a: 'Yes — every style keeps the middle band calm and low-contrast by design, so a caption, sticker or screenshot sits on it cleanly.'},
            {q: 'Can I get a different texture, like a watercolor wash?', a: 'Yes — type it into the direction field and the background is redrawn in the same three moods.'},
        ],
    },
}
